import React from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import type { MathBlock } from '@/types/contentBlocks';

interface MathBlockProps {
  block: MathBlock;
  isDarkMode: boolean;
}

const sanitizeMathContent = (content: string, displayMode: boolean): string => {
  if (!content) return '';

  const ESCAPED_DOLLAR = '<<ESCAPED_DOLLAR>>';

  // Replace problematic Unicode spaces with normal spaces to avoid KaTeX metrics errors
  // Includes: NBSP (00A0), EN/EM/THIN/NARROW/etc. spaces (2000-200A), ZERO-WIDTH (200B), NNBSP (202F)
  const unicodeSpaceRegex = /[\u00A0\u2000-\u200B\u202F]/g;

  let sanitized = content
    .replace(unicodeSpaceRegex, ' ')
    .replace(/\r\n/g, '\n')
    .replace(/\s+$/g, '')
    .replace(/\\\$/g, ESCAPED_DOLLAR)
    // Ensure consecutive fractions or equations have spacing when concatenated
    .replace(/(\}\s*)(\frac|\sqrt|\ce|\sum|\int)/g, '$1\\; $2')
    .replace(/(=)(\frac|\sqrt|\sum|\int)/g, '$1\\; $2');

  // Convert stray $$ separators to line breaks for display math
  sanitized = sanitized.replace(/\$\$+/g, displayMode ? '\\\\' : ' ');

  // Remove any remaining single dollar delimiters
  sanitized = sanitized.replace(/\$/g, ' ');

  sanitized = sanitized
    .replace(new RegExp(ESCAPED_DOLLAR, 'g'), '\\$')
    .replace(/\s{2,}/g, ' ')
    .trim();

  return sanitized;
};

export const MathBlockRenderer: React.FC<MathBlockProps> = ({ block, isDarkMode }) => {
  const [html, setHtml] = React.useState<string>('');
  const [error, setError] = React.useState<string | null>(null);
  const sanitizedContent = React.useMemo(
    () => sanitizeMathContent(block.content, block.displayMode),
    [block.content, block.displayMode]
  );
  React.useEffect(() => {
    try {
      const rendered = katex.renderToString(sanitizedContent, {
        displayMode: block.displayMode,
        throwOnError: false,
        errorColor: isDarkMode ? '#ff9999' : '#cc0000',
        strict: false,
        trust: true,
        output: 'html',
      });
      setHtml(rendered);
      setError(null);
    } catch (err) {
      console.warn('KaTeX rendering error:', err);
      setError(sanitizedContent || block.content);
    }
  }, [sanitizedContent, block.displayMode, isDarkMode, block.content]);

  if (error) {
    return (
      <div
        className={`my-4 rounded-xl border px-4 py-3 text-sm ${
          isDarkMode
            ? 'border-red-900/40 bg-red-900/10 text-red-300'
            : 'border-red-200 bg-red-50 text-red-700'
        }`}
      >
        <code className="block overflow-x-auto whitespace-pre-wrap text-sm">{error}</code>
      </div>
    );
  }

  if (!block.displayMode) {
    return <span className="math-inline mx-1 align-middle text-base" dangerouslySetInnerHTML={{ __html: html }} />;
  }

  return (
    <div
      className={`math-block my-6 overflow-x-auto rounded-xl border px-6 py-4 text-lg leading-relaxed ${
        isDarkMode ? 'border-gray-700 bg-[#171717] text-gray-100' : 'border-border bg-card text-gray-900'
      }`}
    >
      <div className="math-block-inner" dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
};
